const COMMIT_SHA_PATTERN = /^[0-9a-f]{40}$/i;

/**
 * Whether a value is a full commit sha.
 *
 * Exported so each surface can keep its own list of env names while sharing one
 * definition of "valid". Surfaces genuinely differ: Railway injects
 * RAILWAY_GIT_COMMIT_SHA into the cloud shell build, while the desktop release
 * workflows pass VITE_SENTRY_COMMIT_SHA. What must not differ is the check.
 */
export function isFullCommitSha(value: string | undefined): boolean {
  const candidate = value?.trim();
  return candidate !== undefined && candidate !== "" && COMMIT_SHA_PATTERN.test(candidate);
}

/**
 * Build identity from the environment.
 *
 * `.dockerignore` excludes `.git`, so `git rev-parse HEAD` cannot resolve inside
 * the Railway container — the surface where "which commit is live?" is asked
 * most. The commit therefore has to arrive as env there, with git left as the
 * fallback for surfaces that genuinely are checkouts (desktop and dev run from
 * a repo).
 *
 * A cloud runtime is neither: it installs the published `@cmmd-center/forge`
 * tarball, so it has no build variables and no `.git`, and both rungs below
 * return nothing there. That is why the server bakes the commit into its bundle
 * at build time and reads that first — see `apps/server/src/environment/
 * buildIdentity.ts`. This comment previously claimed a sprite clones forge at
 * bootstrap; it does not, and believing it cost a diagnosis.
 *
 * `RAILWAY_GIT_COMMIT_SHA` is injected by the platform, which is what lets this
 * work on the cloud shell with no Dockerfile plumbing. `FORGE_BUILD_COMMIT`
 * takes precedence so any other deployment can set it explicitly.
 *
 * Validated rather than passed through: a descriptor field that reports build
 * identity must never report a plausible-looking non-identity. An absent field
 * honestly means "unknown"; a truncated or placeholder value silently sends
 * someone to diff against the wrong commit.
 */
export function resolveBuildCommitFromEnv(
  env: Readonly<Record<string, string | undefined>>,
): string | undefined {
  for (const key of ["FORGE_BUILD_COMMIT", "RAILWAY_GIT_COMMIT_SHA"] as const) {
    const candidate = env[key]?.trim();
    if (candidate && COMMIT_SHA_PATTERN.test(candidate)) return candidate;
  }
  return undefined;
}
